/**
 * Supabase redirects back from an email link with the failure in the URL when
 * the token is expired or already used, e.g.
 *   /set-password#error=access_denied&error_code=otp_expired&error_description=...
 * No session is created in that case, so the page must say so straight away
 * instead of rendering a form the visitor can never submit.
 *
 * The error can arrive in the hash (implicit flow) or the query string (PKCE).
 * Reading it is done once on mount; the params are then stripped so a refresh
 * does not keep re-reporting an error the visitor has already seen.
 */
export function readAuthLinkError(): string | null {
  if (typeof window === 'undefined') return null

  const fromHash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
  const fromQuery = new URLSearchParams(window.location.search)

  const code =
    fromHash.get('error_code') ??
    fromQuery.get('error_code') ??
    fromHash.get('error') ??
    fromQuery.get('error')

  if (!code) return null

  const description =
    fromHash.get('error_description') ?? fromQuery.get('error_description') ?? ''

  // Leave the path intact, drop the auth params.
  window.history.replaceState({}, '', window.location.pathname)

  return description || code
}

/**
 * How long an emailed auth link lives, in minutes.
 *
 * One of four copies that must move together. The real setting is "Email OTP
 * Expiration" in Lovable's backend settings (1800 s since 15 September 2026);
 * the others are the small print in both email template sets
 * (supabase/templates/ and supabase/functions/_shared/email-templates/) and
 * this constant. `otp_expiry` in supabase/config.toml is a record only:
 * Lovable does not apply that file's [auth] block. A number changed in only
 * one place makes the email promise a deadline the backend does not keep.
 *
 * Nothing the browser can read reports the real setting, so the Team page's
 * "Invite expired" badge is computed from `invite_sent_at` and this number.
 * If they ever disagree, keep the estimate early rather than late: an invite
 * shown as dead while the link still works costs one Resend, whereas
 * "Pending" for hours after the link died leaves an admin with no idea why
 * the invitee cannot sign in.
 */
export const AUTH_LINK_TTL_MINUTES = 30

/**
 * Whether a still-unaccepted invite's link has passed that window.
 *
 * A null `sentAt` returns false: the column is backfilled for every pending
 * profile, so null means the row predates it, and guessing "expired" from
 * missing evidence would show a red badge nothing can explain.
 */
export function isInviteExpired(sentAt: string | null | undefined): boolean {
  if (!sentAt) return false
  const sent = new Date(sentAt).getTime()
  if (Number.isNaN(sent)) return false
  return Date.now() - sent > AUTH_LINK_TTL_MINUTES * 60_000
}

/**
 * A link can also die *while the page is open* — the visitor left the form
 * half-filled, or the single-use token was spent in another tab. The failure
 * then arrives as a Supabase error on submit rather than as URL params, and it
 * must not be reported as a generic "something went wrong": the visitor has to
 * be told the link is dead so they request a new one instead of retyping.
 *
 * Matched on both the machine-readable code and the message, because the three
 * calls involved (`getUser`, a PostgREST update, `updateUser`) do not report a
 * dead session the same way — PostgREST returns a JWT error with no auth code.
 */
export function isExpiredLinkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false

  const code = String(
    (error as { code?: unknown }).code ?? (error as { error_code?: unknown }).error_code ?? ''
  ).toLowerCase()
  if (
    code === 'otp_expired' ||
    code === 'session_not_found' ||
    code === 'session_expired' ||
    code === 'refresh_token_not_found' ||
    code === 'bad_jwt' ||
    code === 'pgrst301'
  ) {
    return true
  }

  const message = String((error as { message?: unknown }).message ?? '').toLowerCase()
  return (
    message.includes('auth session missing') ||
    message.includes('session expired') ||
    message.includes('session not found') ||
    message.includes('token is expired') ||
    message.includes('jwt expired') ||
    message.includes('invalid claim')
  )
}
